// The side menu below 1024 px (release 3.3, lane 2 leftovers).
//
// # WHY AN OVERLAY
//
// From 1024 px the menu is the rail on the left (folded to icons below
// 1440, styles/laptop.css). Under 1024 there is no room for it beside the
// page, and until 3.3 it was laid across the top of the window instead,
// wrapping onto two or three rows of links with no Jump to box, so the page
// began a third of the way down. Now it is closed by default and the Menu
// button at the start of the top bar opens it over the page as the same
// rail: brand, Add a vendor, Jump to, the sections, the foot.
//
// # WHAT OPENING AND CLOSING DO
//
//  - Opening puts the focus in the menu, on the Jump to box (or the menu
//    itself when there is none). On a touch screen (pointer: coarse) it goes
//    on the menu itself: a focused text box would raise the keyboard over
//    the menu every time it opens (review of 13 Oct). The page behind is
//    inert and does not scroll, as behind a dialog (kit/Dialog.tsx,
//    pageBehind).
//  - Escape, the close button and a press outside the menu close it and give
//    the focus back to the Menu button. Escape in a Jump to box that holds
//    text clears the text first (NavJump marks that key as used).
//  - Choosing a screen closes it and puts the focus on the page that opens,
//    as the skip link does; choosing the screen already open does the same
//    (a link pressed, or Enter in Jump to: leave()). A control in the menu
//    that opens a panel on the page (two-step sign-in, the development
//    sign-in) closes it with the focus on that panel (leave(to)).
//  - Widening the window to 1024 px or more closes it: the rail is back.
//
// At 1024 px and wider none of this is reachable: the button is not drawn
// (shell.css) and the menu is never open, so nothing there changes.
//
// The hook is plain React and reads the document only through what it is
// given, so test/narrow-menu.test.mjs mounts it (fixtures/mount.mjs) and
// presses its button.

import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type RefObject } from 'react'
import { Icon } from './ui'

// The narrow layout's own condition (shell.css), one hundredth under the md
// breakpoint so the two sides never both apply.
export const NARROW_MENU = '(max-width: 1023.98px)'
export const MENU_ID = 'side-menu'

type Focusable = { focus: (options?: { preventScroll?: boolean }) => void }
type Page = {
  getElementById: (id: string) => Focusable | null
  documentElement: { style: { overflow: string } }
}

// Where the focus goes once the menu has closed.
type Then = 'button' | 'page' | (() => Focusable | null | undefined) | null

export const COARSE_POINTER = '(pointer: coarse)'

export type NarrowMenu = {
  open: boolean
  // Closes it and gives the focus back to the Menu button.
  dismiss: () => void
  // Closes it and puts the focus on the page, or on what `to` finds (a panel
  // the menu opened). Nothing happens when the menu is not open.
  leave: (to?: () => Focusable | null | undefined) => void
  button: {
    ref: RefObject<HTMLButtonElement | null>
    'aria-expanded': boolean
    'aria-controls': string
    onClick: () => void
  }
  panel: {
    ref: RefObject<HTMLElement | null>
    id: string
    tabIndex: number | undefined
    onKeyDown: (e: KeyboardEvent) => void
    onClick: (e: MouseEvent) => void
  }
}

// `routeKey` changes whenever the screen does (App's route); `page` is the
// document, or a test's stand-in.
export function useNarrowMenu(routeKey: unknown, page: Page = document): NarrowMenu {
  const [open, setOpen] = useState(false)
  const button = useRef<HTMLButtonElement | null>(null)
  const panel = useRef<HTMLElement | null>(null)
  // Where the focus goes once it has closed.
  const then = useRef<Then>(null)

  const close = useCallback((to: Then) => {
    setOpen((was) => {
      if (was) then.current = to
      return false
    })
  }, [])
  const dismiss = useCallback(() => close('button'), [close])
  const leave = useCallback((to?: () => Focusable | null | undefined) => close(to ?? 'page'), [close])

  useEffect(() => {
    if (open) {
      const coarse = typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(COARSE_POINTER).matches
      ;((coarse ? null : page.getElementById('nav-jump')) ?? panel.current)?.focus()
      const html = page.documentElement
      const was = html.style.overflow
      html.style.overflow = 'hidden'
      return () => {
        html.style.overflow = was
      }
    }
    const to = then.current
    then.current = null
    if (to === 'button') button.current?.focus()
    else if (to === 'page') page.getElementById('main')?.focus({ preventScroll: true })
    else if (typeof to === 'function') (to() ?? page.getElementById('main'))?.focus({ preventScroll: true })
    return undefined
  }, [open, page])

  // A new screen: the menu has done its job.
  const seen = useRef(routeKey)
  useEffect(() => {
    if (seen.current === routeKey) return
    seen.current = routeKey
    close('page')
  }, [routeKey, close])

  // Wide again: the rail is back and there is nothing to close.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined
    const narrow = window.matchMedia(NARROW_MENU)
    const onChange = () => {
      if (!narrow.matches) close(null)
    }
    narrow.addEventListener('change', onChange)
    return () => narrow.removeEventListener('change', onChange)
  }, [close])

  return {
    open,
    dismiss,
    leave,
    button: {
      ref: button,
      'aria-expanded': open,
      'aria-controls': MENU_ID,
      onClick: () => (open ? dismiss() : setOpen(true)),
    },
    panel: {
      ref: panel,
      id: MENU_ID,
      // Open, a press on an empty part of the menu keeps the focus in it, so
      // Escape still reaches it. Never set otherwise: the rail is unchanged.
      tabIndex: open ? -1 : undefined,
      onKeyDown: (e) => {
        if (!open || e.key !== 'Escape' || e.defaultPrevented) return
        e.preventDefault()
        dismiss()
      },
      onClick: (e) => {
        if (!open) return
        const target = e.target as { closest?: (s: string) => { getAttribute: (n: string) => string | null } | null }
        const link = target.closest?.('a[href]')
        if (!link) return
        // Another screen closes it when the address changes; the screen
        // already open changes no address, so it is closed here.
        if (link.getAttribute('href') === window.location.hash) close('page')
      },
    },
  }
}

// The button at the start of the top bar, drawn only under 1024 px (shell.css).
export function MenuButton({ menu }: { menu: NarrowMenu }) {
  return (
    <button type="button" className="menu-button" {...menu.button}>
      <Icon name="menu" size={18} /> Menu
    </button>
  )
}
